import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { useUser } from "./UserContext";

const LessonsContext = createContext(null);

/* ================= API base (align with SearchContext) ================= */
const ENV_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  "";
const CLEAN_ENV_BASE = ENV_BASE.replace(/\/+$/, "");
const FALLBACK_BASE = "https://www.doroosonline.co.il";
const API_BASE = CLEAN_ENV_BASE || FALLBACK_BASE;

const DEFAULT_TIMEOUT_MS = 12000;
const DEFAULT_TIMEOUT_MS_POST = 7000;
const url = (path) => `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

/* ================= tiny helpers ================= */
async function parseJSONSafely(res) {
  if (res.status === 204) return {};
  const text = await res.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

async function apiGet(path, { timeout = DEFAULT_TIMEOUT_MS, headers = {} } = {}) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);
  try {
    const res = await fetch(url(path), {
      method: "GET",
      mode: "cors",
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
      headers,
    });
    const data = await parseJSONSafely(res);
    if (!res.ok) {
      const msg =
        data?.error ||
        data?.message ||
        (res.status === 401 ? "שגיאה בהרשאות (401)" : `שגיאת שרת (${res.status})`);
      const err = new Error(msg);
      err.status = res.status;
      err.payload = data;
      throw err;
    }
    return data ?? {};
  } catch (e) {
    if (e.name === "AbortError") throw new Error("זמן ההמתנה לשרת הסתיים. נסה שוב בעוד רגע.");
    if (e instanceof TypeError) {
      const hint = API_BASE
        ? `בדוק שהשרת זמין ב- ${API_BASE} וש-CORS מאפשר את המקור של הלקוח.`
        : "הגדר VITE_API_BASE/REACT_APP_API_BASE כדי לוודא שהלקוח פונה לשרת הנכון.";
      throw new Error(`שגיאת רשת: לא ניתן להגיע לשרת. ${hint}`);
    }
    throw e;
  } finally {
    clearTimeout(t);
  }
}

async function apiSend(
  method,
  path,
  body,
  { timeout = DEFAULT_TIMEOUT_MS_POST, headers = {} } = {}
) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);
  try {
    const res = await fetch(url(path), {
      method,
      mode: "cors",
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await parseJSONSafely(res);
    if (!res.ok) {
      const msg =
        data?.error ||
        data?.message ||
        (res.status === 401 ? "שגיאה בהרשאות (401)" : `שגיאת שרת (${res.status})`);
      const err = new Error(msg);
      err.status = res.status;
      err.payload = data;
      throw err;
    }
    return data ?? {};
  } catch (e) {
    if (e.name === "AbortError") throw new Error("השרת לא הגיב בזמן. נסה שוב.");
    if (e instanceof TypeError) {
      const hint = API_BASE
        ? `בדוק שהשרת זמין ב- ${API_BASE} וש-CORS מאפשר את המקור של הלקוח.`
        : "הגדר VITE_API_BASE/REACT_APP_API_BASE כדי לוודא שהלקוח פונה לשרת הנכון.";
      throw new Error(`שגיאת רשת: לא ניתן להגיע לשרת. ${hint}`);
    }
    throw e;
  } finally {
    clearTimeout(t);
  }
}

/* ✅ Bearer token */
function getBearer() {
  try {
    if (typeof window === "undefined") return null;
    const keys = ["token", "jwt", "JWT", "accessToken", "authToken"];
    for (const k of keys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k);
      if (v) return v;
    }
  } catch {
    // ignore
  }
  return null;
}

const getDeviceId = () => {
  try {
    if (typeof window === "undefined") return "";
    let deviceId = localStorage.getItem("device_id");

    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem("device_id", deviceId);
    }

    return deviceId;
  } catch {
    return "";
  }
};

async function apiGetAuth(path, opts = {}) {
  const token = getBearer();
  if (!token) {
    const err = new Error("Missing bearer token");
    err.status = 401;
    throw err;
  }
  return apiGet(path, {
    ...(opts || {}),
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      ...(opts.headers || {}),
    },
  });
}

async function apiSendAuth(method, path, body, opts = {}) {
  const token = getBearer();
  if (!token) {
    const err = new Error("Missing bearer token");
    err.status = 401;
    throw err;
  }
  return apiSend(method, path, body, {
    ...(opts || {}),
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      ...(opts.headers || {}),
    },
  });
}

/* ================= time helpers ================= */
const pad2 = (n) => String(n).padStart(2, "0");

const toLocalDate = (dateValue, hhmm = "00:00") => {
  if (!dateValue) return null;

  try {
    const raw = String(dateValue).trim();

    let datePart = raw;
    if (raw.includes(" ")) datePart = raw.split(" ")[0];
    if (datePart.includes("T")) datePart = datePart.split("T")[0];

    const parts = datePart.split("-").map(Number);
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) {
      return null;
    }

    let Y, M, D;

    if (String(parts[0]).length === 4) {
      [Y, M, D] = parts;
    } else if (String(parts[2]).length === 4) {
      [D, M, Y] = parts;
    } else {
      return null;
    }

    const [h, m] = String(hhmm || "00:00")
      .slice(0, 5)
      .split(":")
      .map(Number);

    const d = new Date(Y, (M || 1) - 1, D || 1, h || 0, m || 0, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

const monthRange = (ym) => {
  const [yStr, mStr] = String(ym || "").split("-");
  const y = Number(yStr);
  const m = Number(mStr);

  if (!y || !m || m < 1 || m > 12) {
    throw new Error('פורמט חודש לא תקין. צריך "YYYY-MM"');
  }

  const start = `${yStr}-${pad2(m)}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const end = `${yStr}-${pad2(m)}-${pad2(lastDay)}`;

  return { start, end };
};

const deriveRuntimeState = (lesson, now = new Date()) => {
  if (!lesson) return "UNKNOWN";

  const start = toLocalDate(lesson?.date, lesson?.start_time || lesson?.startTime);
  const end = toLocalDate(lesson?.date, lesson?.end_time || lesson?.endTime);

  if (lesson.status === "CANCELLED") return "CANCELLED";
  if (lesson.status === "NO_SHOW") return "NO_SHOW";
  if (lesson.status === "NOT_APPROVED") return "REJECTED";
  if (lesson.status === "NO_RESPONSE") return "PENDING_APPROVAL";

  const hasRecordings =
    (Array.isArray(lesson?.recordings) && lesson.recordings.length > 0) ||
    !!lesson?.recordingUrl ||
    !!lesson?.hasRecordings;

  if (lesson.status === "COMPLETED") {
    return hasRecordings ? "RECORDED" : "ENDED";
  }

  if (!start || !end) return "UNKNOWN";
  if (now < start) return "COUNTDOWN";
  if (now >= start && now <= end) return "STARTABLE";
  if (now > end) return hasRecordings ? "RECORDED" : "ENDED";

  return "UNKNOWN";
};

/* ================= lesson normalizer ================= */
const normalizeRecordings = (rawRecordings) => {
  if (!Array.isArray(rawRecordings)) return [];

  return rawRecordings
    .map((rec, index) => {
      if (!rec || typeof rec !== "object") return null;

      const recordingUrl =
        rec.recordingUrl ||
        rec.recording_url ||
        rec.download_url ||
        rec.url ||
        "";

      if (!recordingUrl) return null;

      return {
        id: rec.id ?? rec.zoomRecordingId ?? rec.zoom_recording_id ?? `rec-${index}`,
        recordingUrl,
        recordingStart: rec.recordingStart ?? rec.recording_start ?? null,
        recordingEnd: rec.recordingEnd ?? rec.recording_end ?? null,
        fileType: rec.fileType ?? rec.file_type ?? null,
        fileExtension: rec.fileExtension ?? rec.file_extension ?? null,
        fileSize: rec.fileSize ?? rec.file_size ?? null,
        zoomMeetingId: rec.zoomMeetingId ?? rec.zoom_meeting_id ?? null,
        zoomRecordingId: rec.zoomRecordingId ?? rec.zoom_recording_id ?? null,
        playUrl: rec.playUrl ?? rec.play_url ?? null,
        status: rec.status ?? null,
        recordingType: rec.recordingType ?? rec.recording_type ?? null,
        createdAt: rec.createdAt ?? rec.created_at ?? null,
      };
    })
    .filter(Boolean);
};

const normalizeLesson = (raw = {}, { currentUser = null, scope = "student" } = {}) => {
  if (!raw || typeof raw !== "object") return raw;

  const l = { ...raw };

  const courseId =
    l.courseId ?? l.course_id ?? l.courseID ?? l.CourseId ?? l.course?.id ?? null;

  const typeRaw = l.type ?? (l.is_private ? "private" : "group") ?? "group";
  const type = String(typeRaw).trim().toLowerCase() === "private" ? "private" : "group";

  const normalizedRecordings = normalizeRecordings(
    l.recordings ?? l.lessonRecordings ?? l.savedRecordings ?? []
  );

  const firstRecordingUrl =
    normalizedRecordings.length > 0
      ? normalizedRecordings[0].recordingUrl
      : l.recordingUrl ?? l.recording_url ?? l.recordUrl ?? "";

  const hasRecordings =
    normalizedRecordings.length > 0 ||
    !!firstRecordingUrl ||
    l.hasRecordings === true ||
    l.has_recording === 1 ||
    l.has_recording === "1" ||
    l.has_recording_flag === 1 ||
    l.has_recording_flag === "1" ||
    l.has_saved_recording === 1 ||
    l.has_saved_recording === "1";

  const purchased =
    typeof l.purchased === "boolean" ? l.purchased : l.purchased === 1 || l.purchased === "1";

  const courseTitle = l.courseTitle ?? l.course_title ?? l.course?.title ?? "";
  const courseSubject = l.courseSubject ?? l.course_subject ?? l.course?.subject ?? "";
  const courseImageUrl = l.courseImageUrl ?? l.course_image ?? l.image ?? l.course?.image ?? null;
  const coursePrice = l.coursePrice ?? l.course_price ?? l.course?.price ?? null;

  const lessonPriceRaw =
    l.lessonPrice ?? l.lesson_price ?? l.sessionPrice ?? l.session_price ?? l.price;

  const lessonPrice =
    lessonPriceRaw == null || lessonPriceRaw === "" ? null : Number(lessonPriceRaw);

  const statusRaw =
    l.status ??
    l.lessonStatus ??
    l.lesson_status ??
    l.sessionStatus ??
    l.session_status ??
    "";

  const statusUpper = String(statusRaw || "").trim().toUpperCase();
  const statusMap = {
    PENDING: "NO_RESPONSE",
    WAITING: "NO_RESPONSE",
    NORESPONSE: "NO_RESPONSE",
    NOTAPPROVED: "NOT_APPROVED",
  };
  const compact = statusUpper.replace(/[_\s-]/g, "");
  const normalizedStatus = statusMap[compact] || statusUpper;

  let teacher = l.teacher;
  if (!teacher || typeof teacher !== "object") {
    teacher = {};
  }

  const tId = teacher.id ?? l.teacher_id ?? l.teacherId ?? l.teacherID ?? null;
  const tFirst =
    teacher.firstName ?? teacher.first_name ?? l.teacher_first_name ?? l.teacherFirstName ?? "";
  const tLast =
    teacher.lastName ?? teacher.last_name ?? l.teacher_last_name ?? l.teacherLastName ?? "";
  const tName =
    teacher.name ??
    l.teacher_name ??
    (tFirst || tLast ? `${tFirst} ${tLast}`.trim() : "");

  const tPic =
    teacher.pictureUrl ??
    teacher.picture_url ??
    teacher.imageUrl ??
    teacher.image_url ??
    teacher.photoUrl ??
    teacher.photo_url ??
    teacher.avatarUrl ??
    teacher.avatar_url ??
    teacher.image ??
    teacher.avatar ??
    teacher.photo ??
    l.teacherPictureUrl ??
    l.teacher_picture_url ??
    l.teacherImageUrl ??
    l.teacher_image_url ??
    l.teacher_image ??
    l.teacher_photo ??
    l.teacher_avatar ??
    l.teacherAvatarUrl ??
    l.teacher_avatar_url ??
    null;

  if (scope === "teacher" && currentUser) {
    const u = currentUser;
    const uFirst = u?.first_name || u?.firstName || "";
    const uLast = u?.last_name || u?.lastName || "";
    const uName = u?.name || u?.fullName || `${uFirst} ${uLast}`.trim();
    const uPic =
      u?.pictureUrl || u?.profileImage || u?.profile_image || u?.avatar || u?.photo || u?.image || null;

    teacher = {
      ...teacher,
      id: tId ?? u?.id ?? null,
      firstName: tFirst || uFirst || "",
      lastName: tLast || uLast || "",
      name: tName || uName || "מורה",
      pictureUrl: tPic || uPic || null,
    };
  } else {
    teacher = {
      ...teacher,
      id: tId,
      firstName: tFirst,
      lastName: tLast,
      name: tName || "מורה",
      pictureUrl: tPic,
    };
  }

  const teacherRatingAvg =
    teacher.ratingAvg ?? teacher.rating ?? (l.teacher_rating != null ? l.teacher_rating : null);

  const teacherRatingCount =
    teacher.ratingCount ?? (l.teacher_rating_count != null ? l.teacher_rating_count : 0);

  const ratingAvg =
    l.ratingAvg ??
    l.rating_avg ??
    l.lesson_rating ??
    (typeof l.rating === "number" ? l.rating : null);

  const ratingCount =
    l.ratingCount ??
    l.rating_count ??
    l.lesson_rating_count ??
    (typeof l.rating_count === "number" ? l.rating_count : 0);

  const myRating = l.myRating ?? l.my_rating ?? l.my_rating_value ?? null;

  const canRateRaw = l.canRate ?? l.can_rate ?? l.can_rate_value ?? null;
  const canRate =
    typeof canRateRaw === "boolean" ? canRateRaw : canRateRaw === 1 || canRateRaw === "1";

  const approvedAt = l.approvedAt ?? l.approved_at ?? null;

  const paymentDeadlineAt =
    l.paymentDeadlineAt ??
    l.payment_deadline_at ??
    null;

  const paymentExpiredRaw =
    l.paymentExpired ??
    l.payment_expired ??
    null;

  const paymentExpired =
    typeof paymentExpiredRaw === "boolean"
      ? paymentExpiredRaw
      : paymentExpiredRaw === 1 || paymentExpiredRaw === "1";

  return {
    ...l,
    courseId,
    type,
    status: normalizedStatus,
    recordingUrl: firstRecordingUrl,
    recordings: normalizedRecordings,
    hasRecordings,
    purchased,
    courseTitle,
    courseSubject,
    courseImageUrl,
    coursePrice,
    price: lessonPrice,
    approvedAt,
    paymentDeadlineAt,
    paymentExpired,
    teacher: {
      ...teacher,
      ratingAvg: teacherRatingAvg == null ? null : teacherRatingAvg,
      ratingCount: teacherRatingCount ?? 0,
    },
    ratingAvg: ratingAvg == null ? null : Number(ratingAvg),
    ratingCount: ratingCount ?? 0,
    myRating: myRating == null ? null : Number(myRating),
    canRate: !!canRate,
  };
};

/* ====================== Provider ====================== */
export const LessonsProvider = ({ children }) => {

  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const [cart, setCart] = useState([]);

  const [privateRequests, setPrivateRequests] = useState([]);
  

  const { isLogedin, role, userType, user, showAlert } = useUser();

  const scope = useMemo(
    () => (role === "teacher" || userType === "teacher" ? "teacher" : "student"),
    [role, userType]
  );

  const pendingPrivateCount = useMemo(() => {
    if (scope !== "teacher") return 0;

    const now = new Date(Date.now() + serverOffsetMs);

    return (privateRequests || []).reduce((acc, lesson) => {
      if (!lesson) return acc;
      if (lesson.type !== "private") return acc;
      if (lesson.status !== "NO_RESPONSE") return acc;
      if (lesson.purchased) return acc;

      const start = toLocalDate(lesson.date, lesson.startTime || lesson.start_time);
      if (start && start < now) return acc;

      return acc + 1;
    }, 0);
  }, [privateRequests, scope, serverOffsetMs]);


  const lastLessonsLoadKeyRef = useRef("");

  const fetchMyLessons = useCallback(
    async (opts = {}) => {
      setLoading(true);
      try {
        const q = new URLSearchParams({
          ...(opts.scope ? { scope: opts.scope } : {}),
          ...(opts.start ? { start: opts.start } : {}),
          ...(opts.end ? { end: opts.end } : {}),
          limit: String(opts.limit || 20),
          page: "1",
        });

        const data = await apiGetAuth(`/api/my/lessons?${q.toString()}`);
        const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];

        const normalized = items.map((l) =>
          normalizeLesson(l, { currentUser: user, scope: opts.scope || scope })
        );

        setLessons(normalized);
        return normalized;
      } finally {
        setLoading(false);
      }
    },
    [user, scope]
  );

  const fetchPrivateRequests = useCallback(async () => {
    const data = await apiGetAuth("/api/private-requests");
    const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];

    const normalized = items.map((l) =>
      normalizeLesson(l, {
        currentUser: user,
        scope: "teacher",
      })
    );

    setPrivateRequests(normalized);
    return normalized;
  }, [user]);

  const fetchMyLessonsPage = useCallback(
    async ({
      scope: requestedScope,
      start = "",
      end = "",
      page = 1,
      limit = 10,
    } = {}) => {
      const q = new URLSearchParams({
        ...(requestedScope ? { scope: requestedScope } : {}),
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        page: String(page || 1),
        limit: String(limit || 10),
      });

      const data = await apiGetAuth(`/api/my/lessons?${q.toString()}`);
      const items = Array.isArray(data?.items) ? data.items : [];

      return {
        items: items.map((l) =>
          normalizeLesson(l, {
            currentUser: user,
            scope: requestedScope || scope,
          })
        ),
        page: Number(data?.page || page || 1),
        limit: Number(data?.limit || limit || 10),
        total: Number(data?.total || 0),
      };
    },
    [user, scope]
  );

  const fetchCourseLessonsPage = useCallback(
    async (
      courseId,
      {
        scope: requestedScope,
        start = "",
        end = "",
        page = 1,
        limit = 10,
      } = {}
    ) => {
      const q = new URLSearchParams({
        ...(requestedScope ? { scope: requestedScope } : {}),
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        page: String(page || 1),
        limit: String(limit || 10),
        // Paged course lessons feed the online classroom: ask the server
        // to leave out private lessons that can't be attended (unpaid
        // after the payment window, or not approved). The Booking page
        // uses fetchCourseLessons (no flag) and still sees all of the
        // student's own requests.
        classroom: "1",
      });

      const data = await apiGetAuth(
        `/api/courses/${encodeURIComponent(courseId)}/lessons?${q.toString()}`
      );

      const items = Array.isArray(data?.items) ? data.items : [];

      return {
        items: items.map((l) =>
          normalizeLesson(l, {
            currentUser: user,
            scope: requestedScope || scope,
          })
        ),
        page: Number(data?.page || page || 1),
        limit: Number(data?.limit || limit || 10),
        total: Number(data?.total || 0),
      };
    },
    [user, scope]
  );

  const fetchCourseLessons = useCallback(
    async (courseId) => {
      const data = await apiGetAuth(`/api/courses/${encodeURIComponent(courseId)}/lessons`);
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      return items.map((l) => normalizeLesson(l, { currentUser: user, scope }));
    },
    [user, scope]
  );

  useEffect(() => {
    let mounted = true;

    getServerTime()
      .then((serverNowMs) => {
        if (!mounted) return;
        setServerOffsetMs(serverNowMs - Date.now());
      })
      .catch((err) => {
        console.warn("[Lessons] failed to sync server time:", err);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const hasToken = !!getBearer();
    if (!hasToken || !isLogedin || !user?.id) return;

    const loadKey = `${user.id}-${scope}`;
    if (lastLessonsLoadKeyRef.current === loadKey) return;

    lastLessonsLoadKeyRef.current = loadKey;

    fetchMyLessons({ scope }).catch((err) => {
      console.error("[Lessons] fetchMyLessons failed:", err);
      showAlert?.(
        err?.message || "שגיאה בטעינת המפגשים מהשרת. בדקו התחברות והרשאות.",
        "warning"
      );
    });
  }, [isLogedin, user?.id, scope, fetchMyLessons, showAlert]);

  const createGroupLesson = useCallback(
    async (payload) => {
      const data = await apiSendAuth("POST", "/api/lessons", payload);
      const lessonRaw = data?.lesson || data;
      if (!lessonRaw) throw new Error("השיעור לא חזר מהשרת.");

      const optimistic = normalizeLesson(lessonRaw, { currentUser: user, scope });
      setLessons((prev) => [optimistic, ...prev]);

      try {
        await fetchMyLessons({ scope });
      } catch (e) {
        console.warn("[Lessons] rehydrate after create failed:", e);
      }

      return optimistic;
    },
    [user, scope, fetchMyLessons]
  );

  const updateGroupLesson = useCallback(
    async (lessonId, payload) => {
      const data = await apiSendAuth("PATCH", `/api/lessons/${encodeURIComponent(lessonId)}`, payload);
      const updatedRaw = data?.lesson || data;
      if (!updatedRaw) throw new Error("השיעור המעודכן לא חזר מהשרת.");

      setLessons((prev) =>
        prev.map((l) =>
          String(l.id) !== String(lessonId)
            ? l
            : normalizeLesson({ ...l, ...updatedRaw }, { currentUser: user, scope })
        )
      );

      try {
        await fetchMyLessons({ scope });
      } catch (e) {
        console.warn("[Lessons] rehydrate after update failed:", e);
      }

      return normalizeLesson(updatedRaw, { currentUser: user, scope });
    },
    [user, scope, fetchMyLessons]
  );

  const createPrivateRequest = useCallback(
    async (payload) => {
      const body = {
        courseId: payload.courseId,
        title: payload.title,
        date: payload.date,
        startTime: payload.startTime,
        endTime: payload.endTime,
        price: payload.price,
        platform: payload.platform || "ZOOM",
        link: payload.link || "",
        teacherId: payload.teacherId,
        course_id: payload.courseId,
        start_time: payload.startTime,
        end_time: payload.endTime,
        teacher_id: payload.teacherId,
      };

      let data;
      try {
        data = await apiSendAuth("POST", "/api/private-requests", body);
      } catch (e) {
        if (e?.status === 404) data = await apiSendAuth("POST", "/api/lessons/private-requests", body);
        else throw e;
      }

      const lessonRaw = data?.lesson || data;
      if (!lessonRaw) throw new Error("בקשת שיעור פרטי לא חזרה מהשרת.");

      const optimistic = normalizeLesson(lessonRaw, { currentUser: user, scope });
      setLessons((prev) => [optimistic, ...prev]);

      setPrivateRequests((prev) => [optimistic, ...prev]);

      try {
        await fetchMyLessons({ scope });
      } catch (e) {
        console.warn("[Lessons] rehydrate after private request failed:", e);
      }

      return optimistic;
    },
    [user, scope, fetchMyLessons]
  );

  const approvePrivateRequest = useCallback(
    async (lessonId) => {
      const data = await apiSendAuth("PATCH", `/api/lessons/${encodeURIComponent(lessonId)}/approve`);
      const updatedRaw = data?.lesson || data || {};

      setLessons((prev) =>
        prev.map((l) =>
          String(l.id) !== String(lessonId)
            ? l
            : normalizeLesson(
                { ...l, ...updatedRaw, status: updatedRaw.status || "APPROVED" },
                { currentUser: user, scope }
              )
        )
      );

      setPrivateRequests((prev) =>
        prev.map((l) =>
          String(l.id) !== String(lessonId)
            ? l
            : normalizeLesson(
                { ...l, ...updatedRaw, status: updatedRaw.status || "APPROVED" },
                { currentUser: user, scope: "teacher" }
              )
        )
      );

      return normalizeLesson(updatedRaw, { currentUser: user, scope });
    },
    [user, scope]
  );

  const rejectPrivateRequest = useCallback(
    async (lessonId) => {
      const data = await apiSendAuth("PATCH", `/api/lessons/${encodeURIComponent(lessonId)}/reject`);
      const updatedRaw = data?.lesson || data || {};

      setLessons((prev) =>
        prev.map((l) =>
          String(l.id) !== String(lessonId)
            ? l
            : normalizeLesson(
                { ...l, ...updatedRaw, status: updatedRaw.status || "NOT_APPROVED" },
                { currentUser: user, scope }
              )
        )
      );

      setPrivateRequests((prev) =>
        prev.map((l) =>
          String(l.id) !== String(lessonId)
            ? l
            : normalizeLesson(
                { ...l, ...updatedRaw, status: updatedRaw.status || "NOT_APPROVED" },
                { currentUser: user, scope: "teacher" }
              )
        )
      );

      return normalizeLesson(updatedRaw, { currentUser: user, scope });
    },
    [user, scope]
  );

  const deleteLesson = useCallback(async (lessonId) => {
    await apiSendAuth("DELETE", `/api/lessons/${encodeURIComponent(lessonId)}`);
    setLessons((prev) => prev.filter((l) => String(l.id) !== String(lessonId)));
    setCart((prev) => prev.filter((id) => String(id) !== String(lessonId)));
  }, []);

  const purchaseLesson = useCallback(
    async (lessonId) => {
      const data = await apiSendAuth("POST", `/api/lessons/${encodeURIComponent(lessonId)}/purchase`);
      const updatedRaw = data?.lesson || data || {};

      setLessons((prev) =>
        prev.map((l) =>
          String(l.id) !== String(lessonId)
            ? l
            : normalizeLesson(
                {
                  ...l,
                  ...updatedRaw,
                  purchased: true,
                  status: updatedRaw.status || (l.type === "private" ? "PAID" : l.status),
                },
                { currentUser: user, scope }
              )
        )
      );

      setCart((prev) => prev.filter((id) => String(id) !== String(lessonId)));
      return data;
    },
    [user, scope]
  );

  const rateLesson = useCallback(
    async (lessonId, rating, comment = "") => {
      const payload = { rating: Number(rating) };
      if (comment && comment.trim()) payload.comment = comment.trim();

      const data = await apiSendAuth("POST", `/api/lessons/${encodeURIComponent(lessonId)}/rating`, payload);
      const updatedRaw = data?.lesson || data?.item || null;

      setLessons((prev) =>
        prev.map((l) => {
          if (String(l.id) !== String(lessonId)) return l;
          const mergedBase = { ...l, myRating: Number(rating), canRate: false };
          if (updatedRaw && typeof updatedRaw === "object") {
            return normalizeLesson({ ...mergedBase, ...updatedRaw }, { currentUser: user, scope });
          }
          return normalizeLesson(mergedBase, { currentUser: user, scope });
        })
      );

      return data;
    },
    [user, scope]
  );

  const fetchLessonParticipants = useCallback(async (lessonId) => {
    const data = await apiGetAuth(`/api/lessons/${encodeURIComponent(lessonId)}/participants`);
    return Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
  }, []);

  const updateLessonAttendance = useCallback(async (lessonId, participantId, attended) => {
    return apiSendAuth(
      "PATCH",
      `/api/lessons/${encodeURIComponent(lessonId)}/participants/${encodeURIComponent(
        participantId
      )}/attendance`,
      { attended: !!attended }
    );
  }, []);

  const newZoomMeeting = useCallback(async (lessonId, { topic, platform = "ZOOM" }) => {
    if (!lessonId || !topic) throw new Error("lessonId ו־topic חובה ליצירת פגישת Zoom");

    try {
      const data = await apiSendAuth("POST", "/api/zoom/meetings", { lessonId, topic, platform });
      if (data && typeof data.code === "number" && data.code <= 0)
        throw new Error(data.message || "יצירת פגישת Zoom נכשלה.");
      return data;
    } catch (err) {
      if (err.status === 401) {
        throw new Error(
          err.message === "Invalid or expired token"
            ? "החיבור לזום פג תוקף – יש לעדכן את פרטי חשבון הזום בהגדרות המורה."
            : err.message || "בעיה בהרשאות בעת יצירת פגישת Zoom."
        );
      }
      throw err;
    }
  }, []);

  const getAvailableMeetings = useCallback(async (lessonId) => {
    if (!lessonId) throw new Error("lessonId חובה כדי לבדוק פגישת Zoom קיימת");
    try {
      const q = new URLSearchParams({ lessonId: String(lessonId) });
      return await apiGetAuth(`/api/zoom/meetings?${q.toString()}`);
    } catch (err) {
      if (err.status === 401 && err.message === "Invalid or expired token") {
        throw new Error("החיבור לזום פג תוקף – יש לעדכן/להתחבר מחדש לחשבון זום בהגדרות הפרופיל.");
      }
      throw err;
    }
  }, []);

  const fetchSavedLessonRecordings = useCallback(async (lessonId) => {
    if (!lessonId) throw new Error("lessonId חובה כדי להביא הקלטות");

    const q = new URLSearchParams({ lessonId: String(lessonId) });
    const data = await apiGetAuth(`/api/zoom/recordings/saved?${q.toString()}`);
    const items = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
    return normalizeRecordings(items);
  }, []);

  const filterByDateRange = useCallback((list, start, end) => {
    if (!start && !end) return list;
    const s = start ? toLocalDate(start) : null;
    const e = end ? toLocalDate(end, "23:59") : null;
    return list.filter((l) => {
      const d = toLocalDate(l?.date);
      if (!d) return false;
      return (!s || d >= s) && (!e || d <= e);
    });
  }, []);

  const getRuntimeState = useCallback((lesson, now = new Date()) => deriveRuntimeState(lesson, now), []);

  const canJoin = useCallback((lesson, role = "student") => {
    const state = deriveRuntimeState(lesson);
    if (role === "teacher") return state === "STARTABLE";
    return state === "STARTABLE" && !!lesson?.purchased;
  }, []);

  const canAddToCart = useCallback((lesson) => {
    if (!lesson) return false;
    if (lesson?.purchased) return false;

    const now = new Date(Date.now() + serverOffsetMs);
    const state = deriveRuntimeState(lesson, now);

    const status = String(lesson?.status || "").trim().toUpperCase();
    const type = String(lesson?.type || "group").trim().toLowerCase();
    const isPrivate = type === "private";

    const hasRecording =
      (Array.isArray(lesson?.recordings) && lesson.recordings.length > 0) ||
      !!lesson?.recordingUrl ||
      !!lesson?.hasRecordings;

    if (status === "CANCELLED") return false;

    if (isPrivate) {
      if (status !== "APPROVED") return false;
      if (lesson?.paymentExpired) return false;
      if (!lesson?.paymentDeadlineAt) return false;
      if (state !== "COUNTDOWN") return false;

      const deadlineMs = new Date(lesson.paymentDeadlineAt).getTime();
      if (!Number.isFinite(deadlineMs)) return false;

      return deadlineMs > now.getTime();
    }

    if (status === "NO_SHOW") return false;
    if (status === "NOT_APPROVED") return false;
    if (status === "NO_RESPONSE") return false;

    if (state === "COUNTDOWN") {
      return true;
    }

    if (state === "ENDED" || state === "RECORDED") {
      return hasRecording;
    }

    return false;
  }, [serverOffsetMs]);

  useEffect(() => {
    setCart((prev) =>
      prev.filter((lessonId) => {
        const lesson = lessons.find((l) => String(l.id) === String(lessonId));
        return lesson && canAddToCart(lesson);
      })
    );
  }, [lessons, canAddToCart]);

  useEffect(() => {
    const hasToken = !!getBearer();

    if (!hasToken || !isLogedin || !user?.id || scope !== "teacher") {
      setPrivateRequests([]);
      return;
    }

    fetchPrivateRequests().catch((err) => {
      console.error("[Lessons] fetchPrivateRequests failed:", err);
    });
  }, [isLogedin, user?.id, scope, fetchPrivateRequests]);

  const isInCart = useCallback((lessonId) => cart.some((id) => String(id) === String(lessonId)), [cart]);

  const addToCart = useCallback((lessonId) => {
    setCart((prev) => {
      const lesson = lessons.find((l) => String(l.id) === String(lessonId));
      if (!lesson) return prev;
      if (!canAddToCart(lesson)) return prev;
      return prev.some((id) => String(id) === String(lessonId)) ? prev : [...prev, lessonId];
    });
  }, [lessons, canAddToCart]);

  const removeFromCart = useCallback((lessonId) => {
    setCart((prev) => prev.filter((id) => String(id) !== String(lessonId)));
  }, []);

  const upsertLesson = useCallback(
    (lesson) => {
      const normalized = normalizeLesson(lesson, { currentUser: user, scope });
      setLessons((prev) => {
        const idx = prev.findIndex((l) => String(l.id) === String(normalized.id));
        if (idx === -1) return [normalized, ...prev];
        const copy = [...prev];
        copy[idx] = { ...copy[idx], ...normalized };
        return copy;
      });
    },
    [user, scope]
  );

  const updateLessonStatus = useCallback((lessonId, status) => {
    setLessons((prev) =>
      prev.map((l) => (String(l.id) === String(lessonId) ? { ...l, status } : l))
    );
  }, []);

  const setRecording = useCallback((lessonId, recordingInput) => {
    setLessons((prev) =>
      prev.map((l) => {
        if (String(l.id) !== String(lessonId)) return l;

        const normalizedRecordings = Array.isArray(recordingInput)
          ? normalizeRecordings(recordingInput)
          : typeof recordingInput === "string" && recordingInput
          ? normalizeRecordings([{ recordingUrl: recordingInput }])
          : [];

        return {
          ...l,
          recordings: normalizedRecordings,
          recordingUrl: normalizedRecordings[0]?.recordingUrl || "",
        };
      })
    );
  }, []);

  const cancelLesson = useCallback(
    async (lessonId) => {
      const data = await apiSendAuth("PATCH", `/api/lessons/${encodeURIComponent(lessonId)}/cancel`, {});
      const updatedRaw = data?.lesson || data || {};

      setLessons((prev) =>
        prev.map((l) => {
          if (String(l.id) !== String(lessonId)) return l;
          return normalizeLesson(
            { ...l, ...updatedRaw, status: updatedRaw.status || "CANCELLED" },
            { currentUser: user, scope }
          );
        })
      );

      setPrivateRequests((prev) =>
        prev.map((l) => {
          if (String(l.id) !== String(lessonId)) return l;
          return normalizeLesson(
            { ...l, ...updatedRaw, status: updatedRaw.status || "CANCELLED" },
            { currentUser: user, scope: "teacher" }
          );
        })
      );

      try {
        await fetchMyLessons({ scope });
      } catch (e) {
        console.warn("[Lessons] rehydrate after cancel failed:", e);
      }

      return normalizeLesson(updatedRaw, { currentUser: user, scope });
    },
    [user, scope, fetchMyLessons]
  );

  const patchLessonsArray = useCallback((list, ids) => {
    if (!Array.isArray(list) || !Array.isArray(ids) || !ids.length) return list;
    return list.map((l) => {
      if (!ids.some((id) => String(id) === String(l.id))) return l;
      const newStatus =
        l.type === "private" && (l.status === "APPROVED" || l.status === "PAID")
          ? "PAID"
          : l.status;
      return { ...l, purchased: true, status: newStatus };
    });
  }, []);

  const markLessonsPurchased = useCallback(
    (lessonIds) => {
      if (!Array.isArray(lessonIds) || !lessonIds.length) return;
      setLessons((prev) => patchLessonsArray(prev, lessonIds));
      setCart((prev) => prev.filter((id) => !lessonIds.some((lid) => String(lid) === String(id))));
    },
    [patchLessonsArray]
  );

  const getTeacherDoneLessonsByMonth = useCallback(async (ym) => {
    const q = new URLSearchParams({ month: String(ym || "") });
    const data = await apiGetAuth(`/api/teacher/reports/done-lessons?${q.toString()}`);
    return Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
  }, []);

  // Supplier equivalent — every DONE lesson across the supplier's
  // whole team (any teacher on their active roster, teaching one of
  // the supplier's own courses), not just the requesting user's own
  // lessons. See supplierTeamDoneLessonsReport() on the backend for
  // why income here is real settled net income, not a coupon's
  // nominal face value.
  const getSupplierTeamDoneLessonsByMonth = useCallback(async (ym, teacherId) => {
    const q = new URLSearchParams({ month: String(ym || "") });
    if (teacherId != null && teacherId !== "") q.set("teacherId", String(teacherId));
    const data = await apiGetAuth(`/api/supplier/reports/done-lessons?${q.toString()}`);
    return Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
  }, []);

  // Per-teacher lesson-outcome counts (completed/cancelled/no-show)
  // across the supplier's whole team for a chosen month — a status
  // summary, not a revenue report, so it isn't filtered to DONE-only
  // the way getSupplierTeamDoneLessonsByMonth is.
  const getSupplierTeacherLessonsSummaryByMonth = useCallback(async (ym) => {
    const q = new URLSearchParams({ month: String(ym || "") });
    const data = await apiGetAuth(`/api/supplier/reports/teacher-lessons-summary?${q.toString()}`);
    return Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
  }, []);

  // Every lesson (any status) for one team teacher in a given month —
  // a plain listing, no income/tax/commission at all. See
  // supplierTeacherAllLessonsReport() on the backend.
  const getSupplierTeacherAllLessonsByMonth = useCallback(async (ym, teacherId) => {
    const q = new URLSearchParams({ month: String(ym || ""), teacherId: String(teacherId || "") });
    const data = await apiGetAuth(`/api/supplier/reports/teacher-lessons?${q.toString()}`);
    return Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
  }, []);

  const getAdminTeachersIncomeReport = useCallback(
    async ({ start, end, search = "", sort = "income_desc", page = 1, limit = 20 } = {}) => {
      const q = new URLSearchParams({
        start: start || "",
        end: end || "",
        search: search || "",
        sort: sort || "income_desc",
        page: String(page || 1),
        limit: String(limit || 20),
      });

      const data = await apiGetAuth(`/api/admin/reports/teachers-income?${q.toString()}`);
      const rawItems = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];

      const items = rawItems.map((t) => {
        const grossIncome = Number(t?.grossIncome ?? t?.income ?? 0);
        const commissionAmount = Number(t?.commissionAmount ?? 0);
        const taxAmount = Number(t?.taxAmount ?? 0);
        const netIncome = Number(t?.netIncome ?? 0);

        return {
          ...t,
          income: grossIncome,
          grossIncome,
          netIncome,
          taxAmount,
          commissionAmount,
          taxPercent: t?.taxPercent ?? null,
          platformCommissionPercent: Number(t?.platformCommissionPercent ?? data?.meta?.platformCommissionPercent ?? 0),
          count: Number(t?.purchasesCount ?? t?.count ?? 0),
          purchasesCount: Number(t?.purchasesCount ?? t?.count ?? 0),
        };
      });

      const totals = data?.totals || {};
      return {
        items,
        total: Number(data?.total ?? items.length ?? 0),
        meta: data?.meta || {},
        totals: {
          income: Number(totals?.income ?? totals?.grossIncome ?? 0),
          count: Number(totals?.count ?? 0),
          teachers: Number(totals?.teachers ?? items.length ?? 0),
          // Rows now include suppliers alongside teachers (role: 'teacher' | 'supplier') —
          // pass these through so the UI can show the split, instead of silently
          // dropping them and leaving the KPI card stuck at 0/0.
          teachersCount: Number(totals?.teachersCount ?? 0),
          suppliersCount: Number(totals?.suppliersCount ?? 0),
          grossIncome: Number(totals?.grossIncome ?? totals?.income ?? 0),
          netIncome: Number(totals?.netIncome ?? 0),
          tax: Number(totals?.tax ?? 0),
          commission: Number(totals?.commission ?? 0),
        },
        raw: data,
      };
    },
    []
  );

  const getAdminTeacherIncomeDetails = useCallback(
    async ({ teacherId, start, end, month, search = "", sort = "income_desc", page = 1, limit = 200 } = {}) => {
      if (!teacherId) throw new Error("Missing teacherId");

      const q = new URLSearchParams({
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        ...(month ? { month } : {}),
        ...(search ? { search } : {}),
        ...(sort ? { sort } : {}),
        page: String(page || 1),
        limit: String(limit || 200),
      });

      const data = await apiGetAuth(`/api/admin/reports/teachers-income/${encodeURIComponent(teacherId)}?${q.toString()}`);
      const items = Array.isArray(data?.items) ? data.items : [];

      return {
        items,
        totals: data?.totals || { income: 0, count: 0 },
        raw: data,
      };
    },
    []
  );

  const getAdminBankTransfersPreview = useCallback(
    async ({ start, end, month = "", search = "" } = {}) => {
      const q = new URLSearchParams({
        ...(month ? { month } : {}),
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        ...(search ? { search } : {}),
      });

      const data = await apiGetAuth(`/api/admin/reports/bank-transfers/preview?${q.toString()}`);

      const items = Array.isArray(data?.items) ? data.items : [];
      const totals = data?.totals || {};
      const meta = data?.meta || {};

      return {
        ok: !!data?.ok,
        items,
        totals: {
          teachers: Number(totals?.teachers ?? items.length ?? 0),
          // Same pass-through as getAdminTeachersIncomeReport — the backend
          // returns both roles now, so the KPI split needs these two too.
          teachersCount: Number(totals?.teachersCount ?? 0),
          suppliersCount: Number(totals?.suppliersCount ?? 0),
          netIncome: Number(totals?.netIncome ?? 0),
          missingBankCount: Number(totals?.missingBankCount ?? 0),
          currency: totals?.currency || "ILS",
        },
        meta: {
          periodStart: meta?.periodStart ?? null,
          periodEnd: meta?.periodEnd ?? null,
          generatedAt: meta?.generatedAt ?? null,
          platformCommissionPercent: meta?.platformCommissionPercent ?? null,
          defaultTaxPercent: meta?.defaultTaxPercent ?? null,
        },
        raw: data,
      };
    },
    []
  );

  const generateBankTransfersPdfAndSendToBank = useCallback(
    async ({ start, end, month = "", search = "" } = {}) => {
      const q = new URLSearchParams({
        ...(month ? { month } : {}),
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        ...(search ? { search } : {}),
      });

      const data = await apiSendAuth("POST", `/api/admin/reports/bank-transfers/pdf?${q.toString()}`, {});

      return {
        ok: !!data?.ok,
        pdfUrl: data?.pdfUrl || "",
        sentToBank: !!data?.sentToBank,
        bankEmail: data?.bankEmail || null,
        message: data?.message || "",
        raw: data,
      };
    },
    []
  );

  const getAdminIncomeTaxReportPreview = useCallback(
    async ({ month, search = "" } = {}) => {
      if (!month) throw new Error('חובה לבחור חודש (YYYY-MM)');

      const q = new URLSearchParams({
        month: String(month || ""),
        ...(search ? { search: String(search) } : {}),
      });

      const data = await apiGetAuth(`/api/admin/reports/income-tax/preview?${q.toString()}`);

      const itemsRaw = Array.isArray(data?.items) ? data.items : [];
      const meta = data?.meta || {};
      const totalsRaw = data?.totals || {};

      const gross = Number(totalsRaw?.gross ?? totalsRaw?.grossIncome ?? 0);
      const commission = Number(totalsRaw?.commission ?? 0);
      const taxWithheld = Number(totalsRaw?.taxWithheld ?? totalsRaw?.tax ?? totalsRaw?.taxPayable ?? 0);
      const afterCommissionTotal = Number(totalsRaw?.afterCommission ?? (gross - commission));

      const items = itemsRaw.map((t) => {
        const grossIncome = Number(t?.grossIncome ?? 0);
        const commissionAmount = Number(t?.commissionAmount ?? 0);
        const afterCommission =
          Number(
            t?.afterCommission ??
              t?.after_commission ??
              (grossIncome - commissionAmount)
          );

        const taxWithheldRow = Number(
          t?.taxWithheld ??
            t?.taxAmount ??
            t?.tax_amount ??
            0
        );

        return {
          ...t,
          teacherId: Number(t?.teacherId ?? t?.teacher_id ?? 0),
          teacherName: t?.teacherName ?? t?.teacher_name ?? "—",
          email: t?.email ?? null,
          phone: t?.phone ?? null,
          idNumber: t?.idNumber ?? t?.id_number ?? null,
          hasIdNumber: !!(t?.hasIdNumber ?? (t?.idNumber ?? t?.id_number)),
          purchasesCount: Number(t?.purchasesCount ?? t?.purchases_count ?? 0),
          grossIncome,
          commissionAmount,
          afterCommission,
          taxPercent: Number(t?.taxPercent ?? t?.tax_percent ?? meta?.defaultTaxPercent ?? 0),
          taxWithheld: taxWithheldRow,
          currency: t?.currency || "ILS",
          ok: !!(t?.ok ?? t?.hasIdNumber ?? (t?.idNumber ?? t?.id_number)),
          statusLabel: t?.statusLabel || (t?.hasIdNumber ? "תקין" : "חסרה ת״ז"),
        };
      });

      return {
        ok: !!data?.ok,
        meta: {
          month: meta?.month ?? month,
          periodStart: meta?.periodStart ?? null,
          periodEnd: meta?.periodEnd ?? null,
          platformCommissionPercent: meta?.platformCommissionPercent ?? null,
          defaultTaxPercent: meta?.defaultTaxPercent ?? null,
          generatedAt: meta?.generatedAt ?? null,
        },
        totals: {
          teachers: Number(totalsRaw?.teachers ?? items.length ?? 0),
          // Same pass-through as the other two admin reports.
          teachersCount: Number(totalsRaw?.teachersCount ?? 0),
          suppliersCount: Number(totalsRaw?.suppliersCount ?? 0),
          gross,
          commission,
          afterCommission: afterCommissionTotal,
          taxWithheld,
          missingCount: Number(totalsRaw?.missingCount ?? totalsRaw?.missing_id_count ?? 0),
          currency: totalsRaw?.currency || "ILS",
        },
        items,
        raw: data,
      };
    },
    []
  );

  const generateIncomeTaxPdfAndSend = useCallback(
    async ({ month, search = "" } = {}) => {
      if (!month) throw new Error('חובה לבחור חודש (YYYY-MM)');

      const q = new URLSearchParams({
        month: String(month || ""),
        ...(search ? { search: String(search) } : {}),
      });

      const data = await apiSendAuth("POST", `/api/admin/reports/income-tax/generate?${q.toString()}`, {});

      return {
        ok: !!data?.ok,
        pdfUrl: data?.pdfUrl || "",
        sentToTaxAuthority: !!data?.sentToTaxAuthority,
        sentToBank: !!data?.sentToBank,
        message: data?.message || "",
        raw: data,
      };
    },
    []
  );

  const markStudentJoinedLesson = useCallback(
    async (lessonId) => {
      const data = await apiSendAuth(
        "PATCH",
        `/api/lessons/${encodeURIComponent(lessonId)}/student-join`,
        {},
        {
          headers: {
            "X-Device-Id": getDeviceId(),
          },
        }
      );

      const lessonPayload = data?.lesson || {};

      setLessons((prev) =>
        prev.map((l) =>
          String(l.id) === String(lessonId)
            ? {
                ...l,
                purchased: true,
                joined: true,
              }
            : l
        )
      );

      return {
        ...data,
        joinUrl: lessonPayload?.joinUrl || data?.joinUrl || "",
      };
    },
    []
  );

  const markLessonRecordingWatched = useCallback(
    async (lessonId) => {
      const normalizedLessonId = Number(lessonId);

      if (!Number.isFinite(normalizedLessonId) || normalizedLessonId <= 0) {
        throw new Error("lessonId is required");
      }

      const data = await apiSendAuth(
        "POST",
        "/api/lessons/recording/watched",
        { lessonId: normalizedLessonId },
        { timeout: 10000 }
      );

      if (!data?.ok) {
        throw new Error(data?.message || "Failed to mark recording as watched");
      }

      setLessons((prev) =>
        Array.isArray(prev)
          ? prev.map((lesson) =>
              Number(lesson?.id) === normalizedLessonId
                ? {
                    ...lesson,
                    canRate: true,
                    watchedRecording: true,
                  }
                : lesson
            )
          : prev
      );

      return data;
    },
    []
  );

  const getServerTime = async () => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);

    try {
      const res = await fetch(`${API_BASE}/api/server-time`, {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
        signal: controller.signal,
      });

      let data = null;

      try {
        data = await res.json();
      } catch {
        throw new Error("Invalid server response while fetching server time.");
      }

      if (!res.ok) {
        throw new Error(
          data?.message || `Failed to fetch server time (HTTP ${res.status}).`
        );
      }

      const serverNowMs = Number(data?.serverNowMs);

      if (!Number.isFinite(serverNowMs) || serverNowMs <= 0) {
        throw new Error("Server time response is missing a valid serverNowMs value.");
      }

      return serverNowMs;
    } catch (err) {
      if (err?.name === "AbortError") {
        throw new Error("Request timed out while fetching server time.");
      }
      throw err instanceof Error
        ? err
        : new Error("Unknown error while fetching server time.");
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const value = useMemo(
    () => ({
      lessons,
      loading,
      fetchMyLessons,
      fetchCourseLessons,
      fetchMyLessonsPage,
      fetchCourseLessonsPage,
      fetchPrivateRequests,
      privateRequests,
      pendingPrivateCount,
      fetchLessonParticipants,
      fetchSavedLessonRecordings,
      getTeacherDoneLessonsByMonth,
      getSupplierTeamDoneLessonsByMonth,
      getSupplierTeacherLessonsSummaryByMonth,
      getSupplierTeacherAllLessonsByMonth,
      markStudentJoinedLesson,
      getAdminTeachersIncomeReport,
      getAdminTeacherIncomeDetails,
      getAdminBankTransfersPreview,
      generateBankTransfersPdfAndSendToBank,
      getAdminIncomeTaxReportPreview,
      generateIncomeTaxPdfAndSend,
      filterByDateRange,
      getRuntimeState,
      getServerTime,
      canJoin,
      canAddToCart,
      createGroupLesson,
      updateGroupLesson,
      createPrivateRequest,
      approvePrivateRequest,
      rejectPrivateRequest,
      deleteLesson,
      purchaseLesson,
      updateLessonAttendance,
      cancelLesson,
      markLessonRecordingWatched,
      rateLesson,
      newZoomMeeting,
      getAvailableMeetings,
      cart,
      isInCart,
      addToCart,
      removeFromCart,
      upsertLesson,
      updateLessonStatus,
      setRecording,
      markLessonsPurchased,
      _internals: { toLocalDate, deriveRuntimeState, pad2, monthRange, normalizeRecordings },
    }),
    [
      lessons,
      loading,
      fetchMyLessons,
      fetchCourseLessons,
      fetchMyLessonsPage,
      fetchCourseLessonsPage,
      fetchPrivateRequests,
      privateRequests,
      pendingPrivateCount,
      fetchLessonParticipants,
      fetchSavedLessonRecordings,
      getTeacherDoneLessonsByMonth,
      getSupplierTeamDoneLessonsByMonth,
      getSupplierTeacherLessonsSummaryByMonth,
      getSupplierTeacherAllLessonsByMonth,
      markStudentJoinedLesson,
      getAdminTeachersIncomeReport,
      getAdminTeacherIncomeDetails,
      getAdminBankTransfersPreview,
      generateBankTransfersPdfAndSendToBank,
      getAdminIncomeTaxReportPreview,
      generateIncomeTaxPdfAndSend,
      filterByDateRange,
      getRuntimeState,
      getServerTime,
      canJoin,
      canAddToCart,
      createGroupLesson,
      updateGroupLesson,
      createPrivateRequest,
      approvePrivateRequest,
      rejectPrivateRequest,
      deleteLesson,
      purchaseLesson,
      updateLessonAttendance,
      cancelLesson,
      markLessonRecordingWatched,
      rateLesson,
      newZoomMeeting,
      getAvailableMeetings,
      cart,
      isInCart,
      addToCart,
      removeFromCart,
      upsertLesson,
      updateLessonStatus,
      setRecording,
      markLessonsPurchased,
    ]
  );

  if (typeof window !== "undefined" && !CLEAN_ENV_BASE) {
    console.warn(
      `[LessonsContext] API base not set in env. Falling back to ${FALLBACK_BASE}. Set VITE_API_BASE / REACT_APP_API_BASE for development.`
    );
  }

  return <LessonsContext.Provider value={value}>{children}</LessonsContext.Provider>;
};

export const useLessons = () => {
  const ctx = useContext(LessonsContext);
  if (!ctx) throw new Error("useLessons must be used within a LessonsProvider");
  return ctx;
};